import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Modal, Input, message, Spin, Empty, Typography, Divider, Progress, Table, Tabs } from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  SafetyOutlined,
  FolderOutlined,
  FileTextOutlined,
  TeamOutlined,
  EyeOutlined,
  RightOutlined,
  StopOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const DetailsWrapper = styled.div`
  padding: 24px;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;

      .back-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        border: 1px solid #d9d9d9;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #01d9a9;
          color: #01d9a9;
        }
      }

      .header-title {
        h2 {
          margin: 0;
          font-size: 24px;
          color: #014c75;
        }
        .engagement-id {
          font-size: 12px;
          color: #8c8c8c;
        }
      }
    }
  }

  .main-card {
    border-radius: 12px;
    margin-bottom: 24px;
  }

  .status-banner {
    padding: 16px 24px;
    border-radius: 8px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.pending {
      background: #fff7e6;
      border: 1px solid #ffd591;
    }

    &.active {
      background: #f6ffed;
      border: 1px solid #b7eb8f;
    }

    &.closure_requested {
      background: #fffbe6;
      border: 1px solid #ffe58f;
    }

    &.closed {
      background: #f5f5f5;
      border: 1px solid #d9d9d9;
    }

    .status-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .status-text {
      font-size: 14px;
      color: #595959;
    }

    .status-actions {
      display: flex;
      gap: 8px;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .health-section {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    .health-card {
      padding: 20px;
      border-radius: 12px;
      background: #fafafa;
      text-align: center;

      .health-label {
        font-size: 13px;
        color: #8c8c8c;
        margin-bottom: 8px;
      }

      .health-value {
        font-size: 18px;
        font-weight: 600;

        &.good {
          color: #52c41a;
        }

        &.warning {
          color: #faad14;
        }

        &.critical {
          color: #ff4d4f;
        }
      }

      .health-icon {
        font-size: 24px;
        margin-bottom: 8px;
      }
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  .info-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .info-item {
    .info-label {
      font-size: 13px;
      color: #8c8c8c;
      margin-bottom: 4px;
    }

    .info-value {
      font-size: 15px;
      color: #262626;
      font-weight: 500;
    }
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .spend-analysis {
    background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;

    .spend-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .spend-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
      }

      .spend-percentage {
        font-size: 24px;
        font-weight: 700;
        
        &.good { color: #52c41a; }
        &.warning { color: #faad14; }
        &.critical { color: #ff4d4f; }
      }
    }

    .spend-details {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-top: 16px;

      .spend-item {
        text-align: center;
        padding: 12px;
        background: white;
        border-radius: 8px;

        .spend-label {
          font-size: 12px;
          color: #8c8c8c;
        }

        .spend-value {
          font-size: 18px;
          font-weight: 600;
          color: #014c75;
        }
      }
    }
  }

  .risk-compliance-section {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    .risk-card, .compliance-card {
      padding: 20px;
      border-radius: 12px;
      border: 1px solid #f0f0f0;

      .card-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        font-weight: 600;
        font-size: 15px;
      }

      .card-content {
        color: #595959;
        font-size: 14px;
      }

      .item-list {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          gap: 8px;

          &:last-child {
            border-bottom: none;
          }
        }
      }

      .no-items {
        color: #52c41a;
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .risk-card {
      background: #fff9f0;

      .card-header {
        color: #d46b08;
      }
    }

    .compliance-card {
      background: #f0fff4;

      .card-header {
        color: #389e0d;
      }
    }
  }

  .closure-reason {
    margin-top: 16px;
    padding: 16px;
    background: #fffbe6;
    border-radius: 8px;
    border: 1px solid #ffe58f;

    .reason-label {
      font-weight: 600;
      color: #d48806;
      margin-bottom: 4px;
    }

    .reason-text {
      color: #595959;
    }
  }

  .locations-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .projects-section, .jobs-section {
    margin-bottom: 24px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    .empty-state {
      text-align: center;
      padding: 40px;
      background: #fafafa;
      border-radius: 12px;
      color: #8c8c8c;
    }
  }

  .project-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
  }

  .project-card {
    background: white;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    padding: 16px;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-color: #01d9a9;
    }

    .project-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .project-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
        margin: 0;
        flex: 1;
      }
      
      .project-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .project-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        
        .meta-label {
          font-size: 12px;
          color: #8c8c8c;
        }

        .meta-value {
          font-size: 13px;
          font-weight: 500;
          color: #262626;
        }
      }
    }

    .project-footer {
      display: flex;
      justify-content: flex-end;
    }
  }

  .jobs-tabs {
    .ant-tabs-nav {
      margin-bottom: 16px;
    }
  }

  .job-table {
    .ant-table-thead > tr > th {
      background: #fafafa;
    }
  }
`;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  closure_requested: "gold",
  closed: "default",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  closure_requested: "Closure Requested",
  closed: "Closed",
};

const projectStatusColors = {
  pending_approval: "orange",
  active: "green",
  rejected: "red",
  completed: "blue",
};

const jobStatusColors = {
  open: "blue",
  in_progress: "processing",
  closed: "default",
  filled: "green",
  cancelled: "red",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "₹0";
  return `₹${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const AdminEngagementDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchEngagement = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.GET_ENGAGEMENT}/${id}?includeRelated=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEngagement(response.data.engagement);
    } catch (error) {
      console.error("Error fetching engagement:", error);
      message.error("Failed to load engagement details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEngagement();
  }, [fetchEngagement]);

  const handleApprove = async () => {
    Modal.confirm({
      title: "Approve Engagement",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: "Are you sure you want to approve this engagement?",
      okText: "Approve",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.APPROVE_ENGAGEMENT}/${id}/approve`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement approved successfully");
          fetchEngagement();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to approve engagement");
        }
      },
    });
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.APPROVE_ENGAGEMENT}/${id}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Engagement rejected");
      setRejectModalVisible(false);
      fetchEngagement();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to reject engagement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseClosure = async () => {
    Modal.confirm({
      title: "Close Engagement",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: "Are you sure you want to close this engagement?",
      okText: "Close Engagement",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.CLOSE_ENGAGEMENT}/${id}/close`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement closed successfully");
          fetchEngagement();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to close engagement");
        }
      },
    });
  };

  const handleRejectClosure = async () => {
    Modal.confirm({
      title: "Reject Closure Request",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: "Are you sure you want to reject the closure request? The engagement will remain active.",
      okText: "Reject Closure",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.REJECT_ENGAGEMENT_CLOSURE}/${id}/reject-closure`,
            { reason: "Rejected by admin" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Closure request rejected - engagement remains active");
          fetchEngagement();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to reject closure");
        }
      },
    });
  };

  const handleCloseDirectly = async () => {
    Modal.confirm({
      title: "Close Engagement",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: "Are you sure you want to close this engagement? This will mark it as closed.",
      okText: "Close Engagement",
      okButtonProps: { style: { background: "#1890ff", borderColor: "#1890ff" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(
            `${API_CONST.CLOSE_ENGAGEMENT}/${id}/close`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement closed successfully");
          fetchEngagement();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to close engagement");
        }
      },
    });
  };

  const handleDeleteEngagement = async () => {
    Modal.confirm({
      title: "Delete Engagement",
      icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to permanently delete this engagement?</p>
          <p style={{ color: "#ff4d4f", fontSize: 13 }}>This action cannot be undone.</p>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(
            `${API_CONST.DELETE_ENGAGEMENT}/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success("Engagement deleted successfully");
          navigate("/admin/engagements");
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to delete engagement");
        }
      },
    });
  };

  const getBudgetProgress = () => {
    const total = parseFloat(engagement?.totalBudget || 0);
    if (!total) return 0;
    const spent = parseFloat(engagement.budgetSpent || 0);
    return Math.round((spent / total) * 100);
  };

  const getBudgetStatus = () => {
    const progress = getBudgetProgress();
    if (progress >= 100) return "critical";
    if (progress >= 80) return "warning";
    return "good";
  };

  const getRisks = () => {
    const risks = [];
    if (engagement?.health?.duration === "critical") {
      risks.push("Engagement is overdue");
    } else if (engagement?.health?.duration === "warning") {
      risks.push("Engagement ending within 30 days");
    }
    if (engagement?.health?.budget === "critical") {
      risks.push("Budget has been exceeded");
    } else if (engagement?.health?.budget === "warning") {
      risks.push("Budget utilization above 80%");
    }
    if (engagement?.totalJobs === 0 && engagement?.status === "active") {
      risks.push("No job requirements created yet");
    }
    return risks;
  };

  const getComplianceStatus = () => {
    const issues = [];
    if (!engagement?.description) {
      issues.push("Missing engagement description");
    }
    if (!engagement?.expectedStartDate) {
      issues.push("Expected start date not set");
    }
    if (!engagement?.totalBudget && (!engagement?.projects || engagement.projects.length === 0)) {
      issues.push("No projects with budget defined yet");
    }
    return issues;
  };

  const jobColumns = [
    {
      title: "Job ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code style={{ fontSize: 12 }}>{id}</Text>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => <Text strong>{role}</Text>,
    },
    {
      title: "Experience",
      dataIndex: "experienceRange",
      key: "experienceRange",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={jobStatusColors[status] || "default"}>
          {status?.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Positions",
      dataIndex: "noOfOpenings",
      key: "noOfOpenings",
      width: 90,
      align: "center",
    },
    {
      title: "Project",
      dataIndex: "projectId",
      key: "projectId",
      width: 120,
      render: (projectId) => projectId ? <Tag color="purple">{projectId}</Tag> : <Text type="secondary">Direct</Text>,
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/admin/job-requirments/job-details/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <DetailsWrapper>
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <Spin size="large" />
        </div>
      </DetailsWrapper>
    );
  }

  if (!engagement) {
    return (
      <DetailsWrapper>
        <Empty description="Engagement not found" />
      </DetailsWrapper>
    );
  }

  const isActive = engagement.status === "active";
  const isPending = engagement.status === "pending_approval";
  const isClosureRequested = engagement.status === "closure_requested";
  const isClosed = engagement.status === "closed";
  const projects = engagement.projects || [];
  const projectJobs = engagement.projectJobs || [];
  const directJobs = engagement.directJobs || [];
  const allJobs = [...projectJobs, ...directJobs];
  const risks = getRisks();
  const complianceIssues = getComplianceStatus();

  return (
    <DetailsWrapper>
      <div className="header">
        <div className="header-left">
          <div className="back-btn" onClick={() => navigate("/admin/engagements")}>
            <ArrowLeftOutlined />
          </div>
          <div className="header-title">
            <h2>{engagement.title}</h2>
            <span className="engagement-id">{engagement.id}</span>
          </div>
        </div>
      </div>

      <Card className="main-card">
        <div className={`status-banner ${engagement.status.replace("_", "")}`}>
          <div className="status-left">
            <Tag color={statusColors[engagement.status]} style={{ fontSize: 14, padding: "4px 12px" }}>
              {statusLabels[engagement.status]}
            </Tag>
            <span className="status-text">
              {isPending && "Awaiting your approval to activate this engagement."}
              {isActive && "This engagement is currently active."}
              {isClosureRequested && "Customer has requested to close this engagement."}
              {engagement.status === "closed" && "This engagement has been closed."}
            </span>
          </div>
          <div className="status-actions">
            {isPending && (
              <>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                  onClick={handleApprove}
                >
                  Approve
                </Button>
                <Button danger icon={<CloseOutlined />} onClick={() => setRejectModalVisible(true)}>
                  Reject
                </Button>
              </>
            )}
            {isActive && (
              <Button
                icon={<StopOutlined />}
                style={{ color: "#1890ff", borderColor: "#1890ff" }}
                onClick={handleCloseDirectly}
              >
                Close Engagement
              </Button>
            )}
            {isClosureRequested && (
              <>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                  onClick={handleCloseClosure}
                >
                  Approve Closure
                </Button>
                <Button danger icon={<CloseOutlined />} onClick={handleRejectClosure}>
                  Reject Closure
                </Button>
              </>
            )}
            {isClosed && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteEngagement}
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {isClosureRequested && engagement.closureReason && (
          <div className="closure-reason">
            <div className="reason-label">Closure Reason from Customer:</div>
            <div className="reason-text">{engagement.closureReason}</div>
          </div>
        )}

        <Row gutter={24}>
          <Col span={16}>
            <div className="section-title">
              <FileTextOutlined /> Engagement Summary
            </div>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Organization</div>
                <div className="info-value">
                  <span
                    className="org-link"
                    onClick={() => navigate(`/admin/organization/${engagement.organizationId || engagement.customerOrganization}`)}
                  >
                    {engagement.organizationName || engagement.customerOrganization || "-"}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Created By</div>
                <div className="info-value">{engagement.createdByName || "-"}</div>
              </div>
            </div>

            <div className="info-grid-4" style={{ marginTop: 16 }}>
              <div className="info-item">
                <div className="info-label">Type</div>
                <div className="info-value">{engagement.engagementType || "-"}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Duration</div>
                <div className="info-value">
                  {engagement.expectedDuration} {engagement.durationUnit || "months"}
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Start Date</div>
                <div className="info-value">{formatDate(engagement.actualStartDate || engagement.expectedStartDate)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">End Date</div>
                <div className="info-value">{formatDate(engagement.expectedEndDate)}</div>
              </div>
            </div>

            {engagement.description && (
              <div style={{ marginTop: 16 }}>
                <div className="info-label">Description</div>
                <Paragraph ellipsis={{ rows: 2, expandable: true }}>{engagement.description}</Paragraph>
              </div>
            )}
          </Col>

          <Col span={8}>
            {isActive && engagement.health && (
              <div className="health-section" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div className="health-card">
                  <ClockCircleOutlined 
                    className="health-icon" 
                    style={{ color: engagement.health.duration === "good" ? "#52c41a" : engagement.health.duration === "warning" ? "#faad14" : "#ff4d4f" }} 
                  />
                  <div className="health-label">Timeline</div>
                  <div className={`health-value ${engagement.health.duration}`}>
                    {engagement.health.duration === "good" && "On Track"}
                    {engagement.health.duration === "warning" && "Ending Soon"}
                    {engagement.health.duration === "critical" && "Overdue"}
                  </div>
                </div>
                <div className="health-card">
                  <DollarOutlined 
                    className="health-icon" 
                    style={{ color: engagement.health.budget === "good" ? "#52c41a" : engagement.health.budget === "warning" ? "#faad14" : "#ff4d4f" }} 
                  />
                  <div className="health-label">Budget</div>
                  <div className={`health-value ${engagement.health.budget}`}>
                    {engagement.health.budget === "good" && "On Track"}
                    {engagement.health.budget === "warning" && "80%+ Used"}
                    {engagement.health.budget === "critical" && "Exceeded"}
                  </div>
                </div>
                <div className="health-card">
                  <CheckCircleOutlined 
                    className="health-icon" 
                    style={{ color: engagement.health.overall === "good" ? "#52c41a" : engagement.health.overall === "warning" ? "#faad14" : "#ff4d4f" }} 
                  />
                  <div className="health-label">Overall</div>
                  <div className={`health-value ${engagement.health.overall}`}>
                    {engagement.health.overall === "good" && "Healthy"}
                    {engagement.health.overall === "warning" && "Attention"}
                    {engagement.health.overall === "critical" && "Critical"}
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Divider />

        <div className="spend-analysis">
          <div className="spend-header">
            <span className="spend-title">
              <DollarOutlined style={{ marginRight: 8 }} />
              Spend Analysis
            </span>
            <span className={`spend-percentage ${getBudgetStatus()}`}>
              {getBudgetProgress()}% Used
            </span>
          </div>
          <Progress
            percent={getBudgetProgress()}
            strokeColor={getBudgetProgress() >= 100 ? "#ff4d4f" : getBudgetProgress() >= 80 ? "#faad14" : "#52c41a"}
            showInfo={false}
            size="small"
          />
          <div className="spend-details">
            <div className="spend-item">
              <div className="spend-label">Total Budget</div>
              <div className="spend-value">{formatCurrency(engagement.totalBudget || 0)}</div>
            </div>
            <div className="spend-item">
              <div className="spend-label">Remaining</div>
              <div className="spend-value">
                {formatCurrency(Math.max(0, parseFloat(engagement.totalBudget || 0) - parseFloat(engagement.budgetSpent || 0)))}
              </div>
            </div>
            <div className="spend-item">
              <div className="spend-label">Spent Till Now</div>
              <div className="spend-value">{formatCurrency(engagement.budgetSpent)}</div>
            </div>
          </div>
        </div>

        <div className="risk-compliance-section">
          <div className="risk-card">
            <div className="card-header">
              <WarningOutlined /> Risks Identified
            </div>
            <div className="card-content">
              {risks.length > 0 ? (
                <ul className="item-list">
                  {risks.map((risk, idx) => (
                    <li key={idx}>
                      <WarningOutlined style={{ color: "#faad14" }} /> {risk}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-items">
                  <CheckCircleOutlined /> No risks identified
                </div>
              )}
            </div>
          </div>

          <div className="compliance-card">
            <div className="card-header">
              <SafetyOutlined /> Compliance Status
            </div>
            <div className="card-content">
              {complianceIssues.length > 0 ? (
                <ul className="item-list">
                  {complianceIssues.map((issue, idx) => (
                    <li key={idx}>
                      <CloseOutlined style={{ color: "#ff4d4f" }} /> {issue}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-items">
                  <CheckCircleOutlined /> All compliance checks passed
                </div>
              )}
            </div>
          </div>
        </div>

        {engagement.country && (
          <div style={{ marginBottom: 16 }}>
            <div className="section-title">
              <EnvironmentOutlined /> Country
            </div>
            <Tag color="geekblue" icon={<EnvironmentOutlined />}>{engagement.country}</Tag>
          </div>
        )}

        {engagement.preferredLocations?.length > 0 && (
          <>
            <div className="section-title">
              <EnvironmentOutlined /> Preferred Locations
            </div>
            <div className="locations-list">
              {engagement.preferredLocations.map((loc, idx) => (
                <Tag key={idx} color="blue" icon={<EnvironmentOutlined />}>
                  {loc}
                </Tag>
              ))}
            </div>
          </>
        )}
      </Card>

      <Card className="main-card projects-section">
        <div className="section-header">
          <div className="section-title">
            <FolderOutlined /> Projects ({projects.length})
          </div>
        </div>

        {projects.length > 0 ? (
          <div className="project-cards">
            {projects.map((project) => (
              <div 
                key={project.id} 
                className="project-card"
                onClick={() => navigate(`/admin/projects/details/${project.id}`)}
              >
                <div className="project-header">
                  <h4 className="project-title">{project.title}</h4>
                  <div className="project-actions">
                    <Tag color={projectStatusColors[project.status]}>
                      {project.status?.replace("_", " ").toUpperCase()}
                    </Tag>
                    <RightOutlined style={{ color: '#01d9a9', fontSize: 14 }} />
                  </div>
                </div>
                <div className="project-meta">
                  <div className="meta-item">
                    <span className="meta-label">Complexity:</span>
                    <span className="meta-value">{project.complexity || "Medium"}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Budget:</span>
                    <span className="meta-value">{formatCurrency(project.budgetAllocated)}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Jobs:</span>
                    <span className="meta-value">{project.totalJobs || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FolderOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No projects created under this engagement</div>
          </div>
        )}
      </Card>

      <Card className="main-card jobs-section">
        <div className="section-header">
          <div className="section-title">
            <TeamOutlined /> Jobs ({allJobs.length})
          </div>
        </div>

        {allJobs.length > 0 ? (
          <Tabs
            className="jobs-tabs"
            defaultActiveKey="all"
            items={[
              {
                key: "all",
                label: `All Jobs (${allJobs.length})`,
                children: (
                  <Table
                    className="job-table"
                    columns={jobColumns}
                    dataSource={allJobs}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                    size="small"
                  />
                ),
              },
              {
                key: "project",
                label: `Project Jobs (${projectJobs.length})`,
                children: (
                  <Table
                    className="job-table"
                    columns={jobColumns}
                    dataSource={projectJobs}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                    size="small"
                  />
                ),
              },
              {
                key: "direct",
                label: `Direct Jobs (${directJobs.length})`,
                children: (
                  <Table
                    className="job-table"
                    columns={jobColumns}
                    dataSource={directJobs}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                    size="small"
                  />
                ),
              },
            ]}
          />
        ) : (
          <div className="empty-state">
            <TeamOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No job requirements created under this engagement</div>
          </div>
        )}
      </Card>

      <Modal
        title="Reject Engagement"
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleReject}
        okText="Reject"
        okButtonProps={{ loading: submitting, danger: true }}
      >
        <Paragraph>Please provide a reason for rejecting this engagement.</Paragraph>
        <TextArea
          rows={4}
          placeholder="Enter rejection reason..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </DetailsWrapper>
  );
};

export default AdminEngagementDetails;
